import {
  CellContext,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table'

import dayjs from 'dayjs'
import { Deal } from 'models/deals'
import { useMemo } from 'react'

import {
  formatInteger,
  formatTwoDecimal,
  formatUsd,
} from '../../../utils/formatting'
import { getCompanyUrl } from '../../../utils/getCompanyUrl'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { TruncatedTextCell } from '../CommonTableCells/TruncatedTextCell'
import {
  AccountOwnerPill,
  CampaignsCell,
  ContactTagCell,
  DistributorsCell,
  GPOsCell,
  StagePill,
  TaglistCell,
} from '../../FbUI/StagePill'
import ExpandedDealsRow from './ExpandedDealsRow'
import apiService from 'services/api'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { featureFlagService } from 'utils/featureFlagService'
import { ContactCompany } from 'models/companies'
import { AlertTriangle } from 'lucide-react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

// Component for showing maintenance alert in LTV cells
function LTVMaintenanceCell() {
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="ltv-maintenance-tooltip">
          Revenue calculations are currently down, please check back soon.
        </Tooltip>
      }
    >
      <div className="flex items-center justify-center">
        <AlertTriangle className="h-4 w-4 text-yellow-500 cursor-help" />
      </div>
    </OverlayTrigger>
  )
}

export function useDealsTableColumns({
  sorting,
  showCompanyColumn,
}: {
  sorting: any
  showCompanyColumn?: boolean
}): ColumnDef<Deal, any>[] {
  const api = apiService()
  const { preferences } = usePreferences()
  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<Deal>()
  const dealColumnHelper = createTableColumnHelper({
    columns: DEAL_COLUMNS,
    tooltips,
  })
  const { enableEmailIntegration, enabledReportDeals, enableDealsLtv1_5 } =
    featureFlagService()
  const columns = useMemo(() => {
    let currentColumns: ColumnDef<Deal, any>[] = [
      {
        id: 'expanded-row',
        header: '',
        cell: (info: CellContext<Deal, unknown>) => {
          const row = info.row.original
          return (
            <ExpandedDealsRow dealId={row.id} productName={row.product_name} />
          )
        },
      },
      ...(showCompanyColumn
        ? [
            columnHelper.accessor('contact_company', {
              ...dealColumnHelper.retrieveColumn('company', {
                meta: {
                  isEditable: false,
                  isFirstColumn: true,
                },
              }),
              header: () => null,
              cell: (info) => {
                const row = info.row.original
                return (
                  <TruncatedLinkCell
                    text={row.contact_company?.name || ''}
                    to={`${getCompanyUrl(row.contact_company as unknown as ContactCompany)}`}
                  />
                )
              },
              size: 350,
            }),
            columnHelper.accessor('identifier', {
              ...dealColumnHelper.retrieveColumn('identifier', {
                meta: {
                  isEditable: true,
                },
              }),
              header: () => 'Deal ID',
              size: 120,
              cell: (info) => {
                const value = info.getValue()
                return <TruncatedTextCell text={value ? `#${value}` : '-'} />
              },
            }),
          ]
        : [
            columnHelper.accessor('identifier', {
              ...dealColumnHelper.retrieveColumn('identifier', {
                meta: {
                  isEditable: true,
                },
              }),
              header: () => 'Deal ID',
              size: 120,
              cell: (info) => {
                const value = info.getValue()
                return <TruncatedTextCell text={value ? `#${value}` : '-'} />
              },
            }),
          ]),
      columnHelper.accessor('product_name', {
        ...dealColumnHelper.retrieveColumn('product', {
          meta: {
            isEditable: showCompanyColumn,
            isFirstColumn: !showCompanyColumn,
          },
        }),
        header: () => (showCompanyColumn ? 'Product' : ''),
        size: 250,

        cell: (info) => (
          <TruncatedTextCell text={info.row.original.product_name} />
        ),
      }),
      columnHelper.accessor('deal_display_name', {
        ...dealColumnHelper.retrieveColumn('deal_name'),
        header: () => 'Deal Name',
        size: 250,
        cell: (info) => {
          const row = info.row.original
          return (
            <TruncatedTextCell
              text={row.deal_display_name || row.contact_company?.name || '-'}
            />
          )
        },
      }),
      columnHelper.accessor('product_variant_name', {
        ...dealColumnHelper.retrieveColumn('product_variant'),
        header: () => 'Product Variant',
        size: 200,
        cell: (info) => {
          const row = info.row.original
          return row.product_variant_name || '-'
        },
      }),
      columnHelper.accessor('company_size', {
        ...dealColumnHelper.retrieveColumn('company_size'),
        size: 100,
        cell: (info) => {
          const value = info.row.original.contact_company.doors_cnt
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('contact_company.category', {
        ...dealColumnHelper.retrieveColumn('category'),
        cell: (info) => {
          return (
            categories?.find(
              (a) => a.id === (info.getValue() as unknown as number)
            )?.name ?? '-'
          )
        },
      }),
      columnHelper.accessor('contact_company.sub_category', {
        ...dealColumnHelper.retrieveColumn('sub_category'),
        cell: (info) => {
          let subCategoryName = '-'
          for (const category of categories ?? []) {
            const subCategory = category.sub_categories.find(
              (subCat) => subCat.id === (info.getValue() as unknown as number)
            )
            if (subCategory) {
              subCategoryName = subCategory.name
              break
            }
          }
          return subCategoryName
        },
      }),
      columnHelper.accessor('sales_stage', {
        ...dealColumnHelper.retrieveColumn('sales_stage'),
        size: 250,
        cell: (info) => {
          if (!info.row?.original?.sales_stage) return <>-</>
          return <StagePill stageId={info.row.original?.sales_stage} />
        },
      }),
      columnHelper.accessor('last_datetime_stage_changed', {
        ...dealColumnHelper.retrieveColumn('last_datetime_stage_changed'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().diff(dayjs(value), 'day')} days`
        },
      }),
      columnHelper.accessor('expected_days_to_close', {
        ...dealColumnHelper.retrieveColumn('expected_days_to_close'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().add(value, 'day').format('MMM DD, YYYY')}`
        },
      }),
      columnHelper.accessor('close_date_override', {
        ...dealColumnHelper.retrieveColumn('close_date_override'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return dayjs(value).format('MMM DD, YYYY')
        },
      }),
      columnHelper.accessor('account_owner', {
        ...dealColumnHelper.retrieveColumn('account_owner'),
        size: 250,
        cell: (info) => {
          return <AccountOwnerPill account_owner_id={info.getValue()} />
        },
      }),
      columnHelper.accessor('days_since_reply', {
        ...dealColumnHelper.retrieveColumn('days_since_reply'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      columnHelper.accessor('days_since_sent', {
        ...dealColumnHelper.retrieveColumn('days_since_sent'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      columnHelper.accessor('origin_campaign', {
        ...dealColumnHelper.retrieveColumn('origin_campaign'),
        size: 260,
        cell: (info) => {
          const campIds = info.row.original.origin_campaign
            ? [info.row.original.origin_campaign]
            : []
          return <CampaignsCell campaignsIds={campIds} />
        },
      }),
      columnHelper.accessor('annual_revenue', {
        ...dealColumnHelper.retrieveColumn('annual_revenue'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('monthly_revenue_predicted', {
        ...dealColumnHelper.retrieveColumn('monthly_revenue_predicted'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('monthly_revenue_override', {
        ...dealColumnHelper.retrieveColumn('monthly_revenue_override'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('annual_volume', {
        ...dealColumnHelper.retrieveColumn('annual_volume'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      columnHelper.accessor('monthly_volume_predicted', {
        ...dealColumnHelper.retrieveColumn('monthly_volume_predicted'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      columnHelper.accessor('monthly_volume_override', {
        ...dealColumnHelper.retrieveColumn('monthly_volume_override'),
        size: 175,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      columnHelper.accessor('annual_brand_opp', {
        ...dealColumnHelper.retrieveColumn('annual_brand_opp'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      columnHelper.accessor('annual_total', {
        ...dealColumnHelper.retrieveColumn('annual_total'),
        cell: (info) => {
          if (!enabledReportDeals && !enableDealsLtv1_5)
            return <LTVMaintenanceCell />
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      columnHelper.display({
        ...dealColumnHelper.retrieveColumn('taglist'),
        size: 260,
        cell: (info) => {
          return (
            <TaglistCell
              taglistIds={info.row.original?.contact_company.taglist}
              tagType="products"
              asc={
                sorting?.length && sorting[0].id === 'taglist'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('contact_tags', {
        ...dealColumnHelper.retrieveColumn('contact_tags'),
        size: 260,
        cell: (info) => {
          return <ContactTagCell deals={[info.row.original]} />
        },
      }),
      columnHelper.accessor('contact_company.distributors', {
        ...dealColumnHelper.retrieveColumn('distributors'),
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell
              distributorsIds={info.row.original?.contact_company.distributors}
              asc={
                sorting?.length && sorting[0].id === 'distributors'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('contact_company.gpos', {
        ...dealColumnHelper.retrieveColumn('gpos'),
        size: 260,
        cell: (info) => {
          return (
            <GPOsCell
              gposIds={info.row.original?.contact_company.gpos}
              asc={
                sorting?.length && sorting[0].id === 'gpos'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('contact_company.note_count', {
        ...dealColumnHelper.retrieveColumn('note_count'),
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('contact_company.contact_count', {
        ...dealColumnHelper.retrieveColumn('contact_count'),
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('days_since_last_modified', {
        ...dealColumnHelper.retrieveColumn('days_since_last_modified'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      columnHelper.accessor('days_since_deal_last_modified', {
        ...dealColumnHelper.retrieveColumn('days_since_deal_last_modified'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      columnHelper.accessor('estimated_meals_per_day', {
        ...dealColumnHelper.retrieveColumn('estimated_meals_per_day'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('operational_schools', {
        ...dealColumnHelper.retrieveColumn('operational_schools'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('modified', {
        ...dealColumnHelper.retrieveColumn('modified'),
        size: 150,
        cell: (info) => {
          return dayjs(info.getValue()).format('MM/DD/YYYY')
        },
      }),
      columnHelper.accessor('created', {
        ...dealColumnHelper.retrieveColumn('created'),
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      }),
      columnHelper.accessor('doors_count', {
        ...dealColumnHelper.retrieveColumn('doors_count'),
        size: 150,
        cell: (info) => info.getValue() || '-',
      }),
    ]

    if (!enableEmailIntegration) {
      currentColumns = currentColumns.filter(
        (column) =>
          column.id !== 'days_since_reply' && column.id !== 'days_since_sent'
      )
    }

    return currentColumns
  }, [categories, tooltips, enabledReportDeals, enableEmailIntegration])

  return columns
}
